import z from "zod";
import { Prisma } from "@prisma/client";
import { propositionAbilityControlSelect } from "../propositions/propositions.vars";
import { constructionSiteClientDetailsSelector } from "../constructionSites/constructionSites.vars";

const ReceiptTypeEnum = {
  NEW_BUSINESS: "NEW_BUSINESS",
  AMENDMENT: "AMENDMENT",
  CANCELLATION: "CANCELLATION",
} as const

export const receiptTypeValues = Object.values(ReceiptTypeEnum)
export type ReceiptType = typeof receiptTypeValues[number]

export const receiptTypeLabels: Record<ReceiptType, string> = {
  [ReceiptTypeEnum.NEW_BUSINESS]: 'AFN',
  [ReceiptTypeEnum.AMENDMENT]: 'AVT',
  [ReceiptTypeEnum.CANCELLATION]: 'ANNUL',
};

const ReceiptStatusValues = {
    PENDING: "PENDING",
    PAID: "PAID",
    REFUNDED: "REFUNDED",
    CANCELLED: "CANCELLED",
} as const;
export type ReceiptStatus = (typeof ReceiptStatusValues)[keyof typeof ReceiptStatusValues];

const ReceiptPaymentTypeEnum = {
  CHECK: "CHECK",
  TRANSFER: "TRANSFER",
} as const;
export const receiptPaymentTypeValues = Object.values(ReceiptPaymentTypeEnum);
export type ReceiptPaymentType = typeof receiptPaymentTypeValues[number];
export const receiptPaymentTypeLabels: Record<ReceiptPaymentType, string> = {
  [ReceiptPaymentTypeEnum.CHECK]: "Chèque",
  [ReceiptPaymentTypeEnum.TRANSFER]: "Virement",
};

const propositionProductTypes = ['DO', 'TRC', 'MRT', 'GFA'] as const;
type PropositionProductType = (typeof propositionProductTypes)[number];

const receiptPaymentSchema = z.object({
  id: z.number(),
  paymentReference: z.string(),
  paymentDate: z.iso.datetime()
    .transform((value) => new Date(value)),
  amount: z.number(),
  type: z.enum(ReceiptPaymentTypeEnum),
  createdAt: z.iso.datetime()
    .transform((value) => new Date(value)),
  updatedAt: z.iso.datetime()
    .transform((value) => new Date(value))
})
type ReceiptPaymentSchema = z.infer<typeof receiptPaymentSchema>;


const receiptDODetailsSchema = z.object({
  id: z.number(),
  receiptId: z.number(),
  decennialLiabilityInsurancePremiumHT: z.number().nullable(),
  decennialLiabilityInsuranceTaxes: z.number().nullable(),
  decennialLiabilityInsurancePremiumTTC: z.number().nullable(),
  existingDamagesPremiumHT: z.number().nullable(),
  existingDamagesTaxes: z.number().nullable(),
  existingDamagesPremiumTTC: z.number().nullable(),
  cnrPremiumHT: z.number().nullable(),
  cnrTaxes: z.number().nullable(),
  cnrPremiumTTC: z.number().nullable(),
  terrorismContributionPremiumHT: z.number().nullable(),
  terrorismContributionTaxes: z.number().nullable(),
  terrorismContributionPremiumTTC: z.number().nullable(),
});
type ReceiptDODetailsSchema = z.infer<typeof receiptDODetailsSchema>;

const receiptMRTDetailsSchema = z.object({
  id: z.number(),
  receiptId: z.number(),
  contractorsAllRisksPremiumHT: z.number().nullable(),
  contractorsAllRisksTaxes: z.number().nullable(),
  contractorsAllRisksPremiumTTC: z.number().nullable(),
  naturalDisastersPremiumHT: z.number().nullable(),
  naturalDisastersTaxes: z.number().nullable(),
  naturalDisastersPremiumTTC: z.number().nullable(),
  terrorismContributionPremiumHT: z.number().nullable(),
  terrorismContributionTaxes: z.number().nullable(),
  terrorismContributionPremiumTTC: z.number().nullable(),
  principalLiabilityInsurancePremiumHT: z.number().nullable(),
  principalLiabilityInsuranceTaxes: z.number().nullable(),
  principalLiabilityInsurancePremiumTTC: z.number().nullable(),
  decennialLiabilityInsurancePremiumHT: z.number().nullable(),
  decennialLiabilityInsuranceTaxes: z.number().nullable(),
  decennialLiabilityInsurancePremiumTTC: z.number().nullable(),
});
type ReceiptMRTDetailsSchema = z.infer<typeof receiptMRTDetailsSchema>;

const receiptTRCDetailsSchema = z.object({
  id: z.number(),
  receiptId: z.number(),
  propertyDamageCoveragePremiumHT: z.number().nullable(),
  propertyDamageCoverageTaxes: z.number().nullable(),
  propertyDamageCoveragePremiumTTC: z.number().nullable(),
  includingFirePremiumHT: z.number().nullable(),
  includingFireTaxes: z.number().nullable(),
  includingFirePremiumTTC: z.number().nullable(),
  includingOtherDamagesPremiumHT: z.number().nullable(),
  includingOtherDamagesTaxes: z.number().nullable(),
  includingOtherDamagesPremiumTTC: z.number().nullable(),
  includingNaturalDisastersPremiumHT: z.number().nullable(),
  includingNaturalDisastersTaxes: z.number().nullable(),
  includingNaturalDisastersPremiumTTC: z.number().nullable(),
  principalLiabilityInsurancePremiumHT: z.number().nullable(),
  principalLiabilityInsuranceTaxes: z.number().nullable(),
  principalLiabilityInsurancePremiumTTC: z.number().nullable(),
  terrorismContributionPremiumHT: z.number().nullable(),
  terrorismContributionTaxes: z.number().nullable(),
  terrorismContributionPremiumTTC: z.number().nullable(),
});
type ReceiptTRCDetailsSchema = z.infer<typeof receiptTRCDetailsSchema>;

const receiptWithExtendedInfoSchema = z.object({
  id: z.number(),
  reference: z.string(),
  type: z.enum(ReceiptTypeEnum),
  productType: z.enum(propositionProductTypes),
  propositionId: z.number(),
  issueDate: z.iso.datetime()
    .transform((str) => new Date(str)),
  duneManagementFeesHT: z.number().nullable(),
  duneManagementFeesTaxes: z.number().nullable(),
  duneManagementFeesTTC: z.number().nullable(),
  intermediaryManagementFeesHT: z.number().nullable(),
  intermediaryManagementFeesTaxes: z.number().nullable(),
  intermediaryManagementFeesTTC: z.number().nullable(),
  brokerCommission: z.number().nullable(),
  brokerRemuneration: z.number().nullable(),
  totalGrossPremium: z.number().nullable(),
  netPremium: z.number().nullable(),
  amountPaid: z.number(),
  outstandingAmount: z.number().nullable(),
  lastPaymentDate: z.iso.datetime()
  .transform((value) => new Date(value)).nullable(),
  status: z.enum(ReceiptStatusValues),
  payments: z.array(receiptPaymentSchema),
  createdAt: z.iso.datetime()
    .transform((value) => new Date(value)),
  updatedAt: z.iso.datetime()
    .transform((value) => new Date(value)),
  // Détails spécifiques selon le type de produit
  doDetails: receiptDODetailsSchema.nullable().optional(),
  mrtDetails: receiptMRTDetailsSchema.nullable().optional(),
  trcDetails: receiptTRCDetailsSchema.nullable().optional(),
})
type ReceiptWithExtendedInfoSchema = z.infer<typeof receiptWithExtendedInfoSchema>;

const receiptsCountSchema = z.object({
	all: z.number(),
	pending: z.number(),
	paid: z.number(),
	refunded: z.number(),
	cancelled: z.number(),
})
type ReceiptsCountSchema = z.infer<typeof receiptsCountSchema>;

// Sélecteur Prisma pour les reçus avec détails
export const receiptWithDetailsSelect = {
	id: true,
	reference: true,
	status: true,
	issueDate: true,
	createdAt: true,
	pricingDetails: {
		select: {
			duneManagementFeesTTC: true,
			intermediaryManagementFeesTTC: true,
			brokerCommission: true,
			brokerRemuneration: true,
      doDetails: {
        select: {
          doPrimeTTC: true,
          existingDamagesPrimeTTC: true,
          cnrPrimeTTC: true,
          terrorismContributionPrimeTTC: true,
        },
      },
      mrtDetails: {
        select: {
          trcPrimeTTC: true,
          trcNaturalDisastersPrimeTTC: true,
          trcTerrorismContributionPrimeTTC: true,
          rcmoPrimeTTC: true,
          doCnrPrimeTTC: true,
        },
      },
      trcDetails: {
        select: {
          damageCoveragePrimeTTC: true,
          damageCoverageFirePrimeTTC: true,
          damageCoverageOtherDamagesPrimeTTC: true,
          damageCoverageNaturalDisastersPrimeTTC: true,
          rcmoPrimeTTC: true,
          terrorismContributionPrimeTTC: true,
        },
      },
		},
	},
	proposition: {
		select: {
			id: true,
			constructionSite: {
				select: {
					id: true,
					constructionSiteDetails: {
						select: {
							address: {
								select: {
									city: true,
								},
							},
						},
					},
				},
			},
		},
	},
} satisfies Prisma.ReceiptSelect;

// Type inféré depuis le sélecteur Prisma
export type ReceiptWithDetails = Prisma.ReceiptGetPayload<{
	select: typeof receiptWithDetailsSelect;
}>;

export const receiptWithPricingDetailsAndPaymentsOrdered = {
  pricingDetails: {
    include: {
      doDetails: true,
      trcDetails: true,
      mrtDetails: true,
    }
  },
  payments: {
    orderBy: {
      paymentDate: "desc",
    },
  },
  proposition: {
    select: {
      constructionSite: {
        select: {
          constructionSiteClientDetails: {
            select: constructionSiteClientDetailsSelector,
          }
        },
      },
      brokerSociety: {
        select: {
          denomination: true,
        },
      },
    },
  },
} satisfies Prisma.ReceiptInclude;
export type ReceiptWithPricingDetailsAndPaymentsOrdered = Prisma.ReceiptGetPayload<{
  include: typeof receiptWithPricingDetailsAndPaymentsOrdered;
}>;

export {
  receiptWithExtendedInfoSchema,
  receiptPaymentSchema,
  receiptDODetailsSchema,
  receiptMRTDetailsSchema,
  receiptTRCDetailsSchema,
  receiptsCountSchema,
  propositionProductTypes,
  ReceiptTypeEnum,
  ReceiptStatusValues,
  ReceiptPaymentTypeEnum,
  type PropositionProductType,
  type ReceiptWithExtendedInfoSchema,
  type ReceiptPaymentSchema,
  type ReceiptDODetailsSchema,
  type ReceiptMRTDetailsSchema,
  type ReceiptTRCDetailsSchema,
  type ReceiptsCountSchema
}


export const receiptAbilityControlSelect = {
  proposition: {
    select: {
      ...propositionAbilityControlSelect,
    }
  },
} satisfies Prisma.ReceiptSelect